import { TypedValue } from '@client/new/helpers/types';
import { toBaseSection } from '../mappers';
import { FeatureCode } from 'generated/graphql';
import { pickObject } from '@client/new/helpers/general';
import {
  DataFields,
  SectionBase,
  SectionConstructor,
  SectionSpec,
} from '../domain';
import { z } from 'zod';
import { toOfferData } from './offer.mappers';

export type CommunityFeature = 'community' | 'directMessages';

export type ServerOfferSection = SectionBase &
  Pick<OfferSection, 'type'> &
  Pick<
    OfferSectionValue,
    'headlineText' | 'programIds' | 'extrasIds' | 'communityFeatures'
  >;

export type OfferSectionValue = SectionBase & {
  headlineText: string;
  programIds: number[];
  extrasIds: string[];
  communityFeatures: FeatureCode[];
};

export type OfferSection = TypedValue<'Offer', OfferSectionValue>;

export type OfferSectionSpec = SectionSpec<
  ServerOfferSection,
  OfferSectionValue,
  DataFields<
    OfferSectionValue,
    'programIds' | 'extrasIds' | 'communityFeatures'
  >
>;

const toDomain: SectionConstructor<'Offer'>['toDomain'] = (section) => ({
  type: 'Offer',
  ...toBaseSection(section),
  ...pickObject(section, [
    'headlineText',
    'programIds',
    'extrasIds',
    'communityFeatures',
  ]),
  headlineText: section.headlineText,
});

const toServer: SectionConstructor<'Offer'>['toServer'] = (section) => ({
  type: 'Offer',
  ...toBaseSection(section),
  ...pickObject(section, [
    'headlineText',
    'programIds',
    'extrasIds',
    'communityFeatures',
  ]),
  headlineText: section.headlineText,
});

const schema: SectionConstructor<'Offer'>['schema'] = z.object({
  type: z.literal('Offer'),
  id: z.string().uuid().optional(),
  bgColor: z.string(),
  enabled: z.boolean(),
  headlineText: z.string().min(1, 'Headline is required'),
  programIds: z.array(z.number()).min(1, 'Select at least one program'),
  extrasIds: z.array(z.string()),
  communityFeatures: z.array(z.nativeEnum(FeatureCode)),
});

export const Offer: SectionConstructor<'Offer'> = {
  toServer,
  toDomain,
  schema,
  toData: toOfferData,
};
